import {
  type Money,
  ZERO,
  money,
  add,
  sub,
  mul,
  sum,
  mulRate,
  clampAtZero,
} from "../money/money";
import { DISCOUNT_CONFLICT_MESSAGE } from "./order-discounts";

/**
 * Cart pricing.
 *
 * Pure: no I/O, no framework imports, no clock, no randomness. The same module
 * runs on the terminal and in the Worker, so a receipt computed offline and the
 * server's revalidation of it are byte-identical by construction rather than by
 * discipline. That is the whole reason this repository is one deployment unit
 * instead of a split frontend and backend.
 *
 * ROUNDING ORDER, fixed and documented (research.md R7):
 *
 *   line extension -> line discount -> line rounding
 *     -> subtotal -> sale-level discount -> tax -> total
 *
 * The sale total is the SUM OF ROUNDED LINE AMOUNTS. Line amounts are never
 * derived by apportioning a rounded total. This is what prevents the classic
 * defect where a receipt's lines do not add up to what the customer paid.
 */

export type TaxRegime = "vat" | "non_vat";

export interface TaxConfig {
  regime: TaxRegime;
  /** Basis points. 1200 = 12%. */
  rateBp: number;
  /** True when shelf prices already contain the tax (the Philippine norm). */
  pricesTaxInclusive: boolean;
}

export interface CartModifierInput {
  id: string;
  name: string;
  priceAdjustment: Money;
}

export interface CartLineInput {
  lineId: string;
  menuItemId: string;
  variantId: string | null;
  itemName: string;
  variantName: string | null;
  unitPrice: Money;
  quantity: number;
  modifiers: CartModifierInput[];
  statutoryEligible: boolean;
  note: string | null;
  /** Line-level promotional discount, already resolved to an amount. */
  discount?: Money;
}

export interface PricedLine {
  lineId: string;
  menuItemId: string;
  variantId: string | null;
  itemName: string;
  variantName: string | null;
  quantity: number;
  unitPrice: Money;
  /** PER UNIT, not extended by quantity. */
  modifierTotal: Money;
  /** (unitPrice + modifierTotal) * quantity */
  lineSubtotal: Money;
  lineDiscount: Money;
  lineTotal: Money;
  statutoryEligible: boolean;
  note: string | null;
  modifiers: CartModifierInput[];
}

export interface PricedCart {
  lines: PricedLine[];
  subtotal: Money;
  discountTotal: Money;
  statutoryDiscountTotal: Money;
  taxableAmount: Money;
  taxExemptAmount: Money;
  zeroRatedAmount: Money;
  taxAmount: Money;
  total: Money;
  taxRegime: TaxRegime;
  taxRateBp: number;
}

export interface PricingOptions {
  /** Sale-level promotional discount, already resolved to an amount. */
  saleDiscount?: Money;
  /**
   * A statutory discount, PRE-COMPUTED by `computeStatutoryDiscount`
   * (statutory.ts) against this same cart's eligible-line total. `priceCart`
   * only applies the resulting figures to the sale's tax split — it does not
   * compute the discount itself, because statutory.ts depends on this file
   * (for `stripVat`/`TaxConfig`) and a runtime import back would cycle.
   * `import type` here is erased at compile time, so this reference alone
   * cannot cycle at runtime; see statutory.ts's own module doc.
   *
   * MUTUALLY EXCLUSIVE with `saleDiscount` (FR-032, order-discounts.ts) — a
   * sale carries a promotional discount or a statutory one, never both.
   */
  statutoryDiscount?: import("./statutory").StatutoryDiscountResult;
}

export class PricingError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "PricingError";
  }
}

/** Price one line. Exported for the till's per-line display. */
export function priceLine(input: CartLineInput): PricedLine {
  if (!Number.isInteger(input.quantity) || input.quantity < 0) {
    throw new PricingError(
      `Line ${input.lineId}: quantity must be a whole number, got ${input.quantity}`,
    );
  }

  const modifierTotal = sum(input.modifiers.map((m) => m.priceAdjustment));
  const perUnit = clampAtZero(add(input.unitPrice, modifierTotal));

  // 1. Line extension.
  const lineSubtotal = mul(perUnit, input.quantity);

  // 2. Line discount, capped so a line can never go negative.
  const requested = input.discount ?? ZERO;
  const lineDiscount = requested > lineSubtotal ? lineSubtotal : requested;

  // 3. Line rounding. Both operands are already whole centavos, so this is
  //    exact — but the step is named because the ORDER is what matters.
  const lineTotal = clampAtZero(sub(lineSubtotal, lineDiscount));

  return {
    lineId: input.lineId,
    menuItemId: input.menuItemId,
    variantId: input.variantId,
    itemName: input.itemName,
    variantName: input.variantName,
    quantity: input.quantity,
    unitPrice: input.unitPrice,
    modifierTotal,
    lineSubtotal,
    lineDiscount,
    lineTotal,
    statutoryEligible: input.statutoryEligible,
    note: input.note,
    modifiers: input.modifiers,
  };
}

export function priceCart(
  inputs: readonly CartLineInput[],
  tax: TaxConfig,
  options: PricingOptions = {},
): PricedCart {
  const lines = inputs.map(priceLine);

  // 4. Subtotal is the sum of ROUNDED line totals — never the other way round.
  const subtotal = sum(lines.map((l) => l.lineTotal));
  const lineDiscountTotal = sum(lines.map((l) => l.lineDiscount));

  if (options.statutoryDiscount) {
    if (options.saleDiscount) {
      throw new PricingError(DISCOUNT_CONFLICT_MESSAGE);
    }
    return priceCartWithStatutoryDiscount(lines, subtotal, lineDiscountTotal, tax, options.statutoryDiscount);
  }

  // 5. Sale-level discount, capped at the subtotal.
  const requestedSaleDiscount = options.saleDiscount ?? ZERO;
  const saleDiscount = requestedSaleDiscount > subtotal ? subtotal : requestedSaleDiscount;
  const discountTotal = add(lineDiscountTotal, saleDiscount);

  const net = clampAtZero(sub(subtotal, saleDiscount));

  // 6. Tax.
  const taxed = computeTax(net, tax);

  return {
    lines,
    subtotal,
    discountTotal,
    statutoryDiscountTotal: ZERO,
    taxableAmount: taxed.taxableAmount,
    taxExemptAmount: taxed.taxExemptAmount,
    zeroRatedAmount: taxed.zeroRatedAmount,
    taxAmount: taxed.taxAmount,
    total: taxed.total,
    taxRegime: tax.regime,
    taxRateBp: tax.rateBp,
  };
}

/**
 * The statutory-discount branch (US5, FR-028–FR-032). Split from the
 * ordinary path above rather than interleaved with it, so the far more
 * common no-statutory-discount sale (every sale, until a business enables
 * this) reads exactly as it always has.
 *
 * The eligible/ineligible split happens HERE, by filtering `lines` on
 * `statutoryEligible` — not via eligibility.ts's identical-looking helper,
 * because importing that file would import statutory.ts's whole dependency
 * chain into price-cart.ts, and statutory.ts already imports THIS file. The
 * filter is one line; duplicating it is cheaper than a cycle.
 */
function priceCartWithStatutoryDiscount(
  lines: PricedLine[],
  subtotal: Money,
  lineDiscountTotal: Money,
  tax: TaxConfig,
  stat: import("./statutory").StatutoryDiscountResult,
): PricedCart {
  const eligibleAmount = sum(lines.filter((l) => l.statutoryEligible).map((l) => l.lineTotal));
  const ineligibleAmount = sub(subtotal, eligibleAmount);

  // The non-statutory remainder — ineligible items, plus whatever share of
  // the eligible items wasn't covered on a shared bill — is taxed exactly
  // as any ordinary sale is.
  const normalPortion = add(ineligibleAmount, stat.uncoveredAmount);
  const taxed = computeTax(normalPortion, tax);

  return {
    lines,
    subtotal,
    discountTotal: add(lineDiscountTotal, stat.discountAmount),
    statutoryDiscountTotal: stat.discountAmount,
    taxableAmount: taxed.taxableAmount,
    taxExemptAmount: add(taxed.taxExemptAmount, stat.exemptedVat),
    zeroRatedAmount: taxed.zeroRatedAmount,
    taxAmount: taxed.taxAmount,
    total: add(taxed.total, stat.amountDue),
    taxRegime: tax.regime,
    taxRateBp: tax.rateBp,
  };
}

/**
 * Strip a rate from a tax-inclusive amount: taxable = amount * 10000 /
 * (10000 + rateBp), half away from zero. Exported so the statutory-discount
 * engine (statutory.ts) reuses this exact rounding rather than a second,
 * independently-drifting implementation of the same formula (US5).
 */
export function stripVat(amount: Money, rateBp: number): Money {
  const denominator = 10_000 + rateBp;
  const scaled = amount * 10_000;
  const quotient = Math.trunc(scaled / denominator);
  const remainder = Math.abs(scaled % denominator);
  return money(remainder * 2 >= denominator ? quotient + (scaled < 0 ? -1 : 1) : quotient);
}

interface TaxResult {
  taxableAmount: Money;
  taxExemptAmount: Money;
  zeroRatedAmount: Money;
  taxAmount: Money;
  total: Money;
}

/**
 * Tax strategies behind one shape (FR-027a).
 *
 * Under tax-INCLUSIVE pricing the tax is derived as the RESIDUAL
 * (net - taxable) rather than computed independently. Computing both from the
 * rate would let rounding push their sum a centavo away from what the customer
 * actually handed over. Deriving one from the other makes
 * `taxable + tax === total` true for every possible amount.
 */
function computeTax(net: Money, tax: TaxConfig): TaxResult {
  if (tax.regime === "non_vat") {
    // Percentage-tax businesses show no VAT breakdown at all (FR-027b). The
    // percentage tax itself is a periodic filing, not a per-receipt line.
    return {
      taxableAmount: ZERO,
      taxExemptAmount: ZERO,
      zeroRatedAmount: ZERO,
      taxAmount: ZERO,
      total: net,
    };
  }

  if (tax.pricesTaxInclusive) {
    const taxableAmount = stripVat(net, tax.rateBp);

    return {
      taxableAmount,
      taxExemptAmount: ZERO,
      zeroRatedAmount: ZERO,
      taxAmount: sub(net, taxableAmount),
      total: net,
    };
  }

  const taxAmount = mulRate(net, tax.rateBp);
  return {
    taxableAmount: net,
    taxExemptAmount: ZERO,
    zeroRatedAmount: ZERO,
    taxAmount,
    total: add(net, taxAmount),
  };
}
